import apiConfig from "app/api/apiConfig.json";
import { getLocalApi } from "../api/localApi";
import { SaveSchoolParams } from "app/shared/model";
import { ApiErrors } from "../api/ApiErrors";

export interface ApiResult<T = any> {
  ok: boolean;
  data?: T;
  message?: string;
  errors?: string[];
  status?: number;
}

async function request(
  url: string,
  method: string,
  payload: SaveSchoolParams
): Promise<ApiResult> {
  try {
    const localApi = await getLocalApi();
    const response = await localApi.call(url, method, JSON.stringify(payload));

    if (!response) {
      return { ok: false, message: "No response from server" };
    }

    const { ok, status } = response as Response;

    let rawText = '';
    try {
      rawText = await (response as Response).text();
    } catch (error) {
      rawText = '';
    }

    let data: any;
    if (rawText) {
      try {
        data = JSON.parse(rawText);
      } catch (error) {
        data = rawText;
      }
    }

    if (!ok) {
      let errors: string[] = [];
      const seen = new Map<string, string>();
      
      if (data) {
        const reasonsArray = data.reasons || data.Reasons;
        if (Array.isArray(reasonsArray) && reasonsArray.length > 0) {
          for (const item of reasonsArray) {
            const msg = item?.message || item?.Message;
            if (msg && typeof msg === 'string') {
              const trimmed = msg.trim();
              if (trimmed) {
                const normalized = trimmed.toLowerCase().replace(/\s+/g, ' ');
                if (!seen.has(normalized)) {
                  seen.set(normalized, trimmed);
                  errors.push(trimmed);
                }
              }
            }
          }
        }
        
        if (errors.length === 0) {
          const errorsArray = data.errors || data.Errors;
          if (Array.isArray(errorsArray) && errorsArray.length > 0) {
            for (const item of errorsArray) {
              const msg = item?.message || item?.Message;
              if (msg && typeof msg === 'string') {
                const trimmed = msg.trim();
                if (trimmed) {
                  const normalized = trimmed.toLowerCase().replace(/\s+/g, ' ');
                  if (!seen.has(normalized)) {
                    seen.set(normalized, trimmed);
                    errors.push(trimmed);
                  }
                }
              }
            }
          }
        }
        
        if (errors.length === 0) {
          errors = ApiErrors.extractAllErrors(data, response as Response, "Failed to save school");
        }
      } else {
        errors = ApiErrors.extractAllErrors(data, response as Response, "Failed to save school");
      }
      
      const message = ApiErrors.formatErrors(errors);
      return { ok: false, message, errors, status, data };
    }

    return { ok: true, data, status };
  } catch (error: any) {
    return { ok: false, message: error?.message || "Failed to save school" };
  }
}

export default async function saveSchool(school: SaveSchoolParams): Promise<ApiResult> {
  const endpoint = apiConfig.schools_create;
  return request(endpoint, "POST", school);
}

export async function updateSchool(school: SaveSchoolParams): Promise<ApiResult> {
  if (!school.id) {
    throw new Error("School id is required to update a school");
  }
  const template = apiConfig.schools_get_by_id;
  const endpoint = template.replace("{id}", encodeURIComponent(String(school.id)));
  return request(endpoint, "PUT", school);
}